// H-1, H-2, H-3: Hiring's landing view.
//
// Hiring is its own top-level section, separate from the learner and admin
// areas, because it is a sandbox: nothing a candidate does reaches the org's
// scores, insights, digests or seat counts (PRD 6.2), and nothing in the rest
// of the product reaches a candidate (6.1, C-9). Keeping it apart in the nav is
// the visible half of that.
//
// The first thing a Team Owner meets here is the "Using AI in hiring" notice
// (L-11), which the backend requires before it accepts any write. They can
// read the list behind it, so the section is never a dead end, but they cannot
// act until they have confirmed it.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { Bone, fadeInSx } from '../../../../components/ui/DashboardSkeletons';
import { hiringApi, hiringMessage, type HiringRoundView, type RoundStatus } from '../../../../api/hiring';
import { useHiringAccess, useHiringOrg, useHiringRounds, useRefreshHiring } from '../../../../queries/useHiring';
import { ActionButton, BODY, BRANDY, DANGER, FONT, LABEL, Note, TEXT, TEXT_3 } from '../capability-setup/designSystem';
import { ScopeFilter, TabButton, type Scope } from '../Program/components/program tabs/InsightsPage';
import CreateRoundDialog from './CreateRoundDialog';
import OwnerNoticeDialog from './OwnerNoticeDialog';
import { Empty, GRID_LINE, HEAD_LINE, day, defaultWelcome } from './hiringParts';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';

// The page dresses as Insights: one bar of tabs and a filter across the top, then the
// rounds in a brandy-outlined panel that fills the rest of the screen.
const BRANDY_LINE = 'rgba(247, 148, 32, 0.45)';
const DIVIDE = { width: '1px', height: 18, flexShrink: 0, bgcolor: 'rgba(247, 148, 32, 0.4)' };
const STATUS_TABS: Array<{ key: RoundStatus; label: string }> = [
  { key: 'open', label: 'Open' },
  { key: 'draft', label: 'Draft' },
  { key: 'closed', label: 'Closed' }
];

// One grid for the header and every round, so each fact sits in its own column.
const RED = '#FF6A55';
const YELLOW = '#FACC15';
const GREEN = '#22C55E';
const COUNT_COLUMNS: Array<{ key: keyof HiringRoundView['counts']; label: string; color: string }> = [
  { key: 'invited', label: 'Invited', color: RED },
  { key: 'started', label: 'In progress', color: YELLOW },
  { key: 'completed', label: 'Completed', color: GREEN },
  { key: 'hired', label: 'Hired', color: GREEN },
  { key: 'unsuccessful', label: 'Unsuccessful', color: RED }
];
// Each column is as wide as its content and the space left over is shared equally between
// them. The rows are subgrids of this, so every row keeps the same columns.
const COLUMNS = {
  display: 'grid',
  gridTemplateColumns: `repeat(${COUNT_COLUMNS.length + 6}, auto)`,
  width: '100%',
  minWidth: 'max-content'
};
const ROW = {
  display: 'grid',
  gridColumn: '1 / -1',
  gridTemplateColumns: 'subgrid',
  boxSizing: 'border-box' as const,
  // The title row and the round rows are one height.
  minHeight: 50,
  '& > *:not(:first-of-type)': { borderLeft: GRID_LINE }
};
// A cell fills its column, so the lines between columns run the full height of the row.
const FILL = { display: 'flex', alignItems: 'center', px: '12px', minWidth: 0, whiteSpace: 'nowrap' };
const CENTRED = { justifyContent: 'center', textAlign: 'center' as const };
// A draft has not opened yet, so that column says when it was created.
const FIRST_DATE_LABEL: Record<RoundStatus, string> = { open: 'Opened', draft: 'Created', closed: 'Opened' };
const firstDate = (round: HiringRoundView) => (round.status === 'draft' ? round.createdAt : round.openedAt);
const CELL = { ...BODY, ...FILL, fontSize: 12.5 };
const NUMBER = { ...CELL, ...CENTRED, color: TEXT, fontVariantNumeric: 'tabular-nums' };

const RoundHeader: React.FC<{ status: RoundStatus }> = ({ status }) => {
  const head = { ...LABEL, ...FILL, color: TEXT };
  return (
    <Box
      sx={{
        ...ROW,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: '#1C1C1C',
        borderBottom: HEAD_LINE
      }}
    >
      <Box sx={head}>Round</Box>
      <Box sx={head}>Role</Box>
      <Box sx={head}>Team</Box>
      <Box sx={{ ...head, ...CENTRED }}>Location</Box>
      {COUNT_COLUMNS.map((c) => (
        <Box key={c.key} sx={{ ...head, ...CENTRED }}>
          {c.label}
        </Box>
      ))}
      <Box sx={{ ...head, ...CENTRED }}>{FIRST_DATE_LABEL[status]}</Box>
      <Box sx={{ ...head, ...CENTRED }}>Closed</Box>
    </Box>
  );
};

const RoundRow: React.FC<{ round: HiringRoundView; last: boolean; onOpen: () => void }> = ({ round, last, onOpen }) => (
  // A div, not a <button>: a button can't pass the shared columns down to its contents.
  <Box
    role="button"
    tabIndex={0}
    onClick={onOpen}
    onKeyDown={(e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    }}
    sx={{
      ...ROW,
      textAlign: 'left',
      borderBottom: last ? 'none' : GRID_LINE,
      cursor: 'pointer',
      transition: 'background-color 0.15s',
      '&:hover': { bgcolor: 'rgba(247, 148, 32, 0.05)' }
    }}
  >
    <Typography sx={{ ...CELL, fontFamily: FONT, fontSize: 14, fontWeight: 600, color: BRANDY }} title={round.name}>
      {round.name}
    </Typography>
    <Typography sx={CELL} title={round.roleName}>
      {round.roleName}
    </Typography>
    <Typography sx={CELL}>{round.teamName || '-'}</Typography>
    <Typography sx={{ ...CELL, ...CENTRED }}>
      {[round.hiringLocation.country, round.hiringLocation.state].filter(Boolean).join('-') || '-'}
    </Typography>
    {COUNT_COLUMNS.map((c) => (
      <Typography key={c.key} sx={{ ...NUMBER, color: round.counts[c.key] ? c.color : TEXT_3 }}>
        {round.counts[c.key]}
      </Typography>
    ))}
    <Typography sx={{ ...CELL, ...CENTRED, color: TEXT_3 }}>{day(firstDate(round))}</Typography>
    <Typography sx={{ ...CELL, ...CENTRED, color: TEXT_3 }}>{day(round.closedAt)}</Typography>
  </Box>
);

const HiringPage: React.FC = () => {
  const navigate = useNavigate();
  const org = useHiringOrg();
  const refresh = useRefreshHiring();
  const access = useHiringAccess();
  const isMobileApp = useIsMobileApp();
  const [tab, setTab] = useState<RoundStatus>('open');
  // Every round once; the tab and the role filter narrow it here.
  const rounds = useHiringRounds();
  const [noticeOpen, setNoticeOpen] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsNotice = Boolean(access.data?.acknowledgementRequired);
  const roles = useMemo(() => access.data?.roles || [], [access.data]);
  // The org's roles, plus any a round was run for that is no longer among them.
  const roleOptions = useMemo(() => {
    const byId = new Map(roles.map((r) => [r.id, { id: r.id, name: r.name }]));
    (rounds.data || []).forEach((r) => {
      if (!byId.has(r.roleId)) byId.set(r.roleId, { id: r.roleId, name: r.roleName });
    });
    return Array.from(byId.values());
  }, [roles, rounds.data]);
  // Every role is picked on arrival; null until the owner chooses otherwise.
  const [chosenRoles, setRoleScope] = useState<Scope | null>(null);
  const roleScope: Scope = chosenRoles ?? roleOptions.map((r) => r.id);
  const shown = useMemo(
    () =>
      (rounds.data || []).filter(
        (r) => r.status === tab && (chosenRoles === null || (chosenRoles !== 'org' && chosenRoles.includes(r.roleId)))
      ),
    [rounds.data, tab, chosenRoles]
  );
  const filtered = Array.isArray(chosenRoles) && chosenRoles.length < roleOptions.length;
  const readyRoles = roles.filter((r) => r.ready);
  // How long data is kept depends on each round's location, so a number only shows when they all agree.
  const retentionMonths = Array.from(new Set((rounds.data || []).map((r) => r.retentionMonths)));
  const retention =
    retentionMonths.length === 1 ? `${retentionMonths[0]} months` : "the period set by its round's location";

  const confirmNotice = async () => {
    if (!access.data) return;
    setBusy(true);
    setError(null);
    try {
      await hiringApi.acknowledge(access.data.ownerAcknowledgement.version, org);
      await access.refetch();
      setNoticeOpen(false);
    } catch (err) {
      setError(hiringMessage(err, 'The notice could not be confirmed'));
    } finally {
      setBusy(false);
    }
  };

  const create = async (input: Parameters<typeof hiringApi.createRound>[0]) => {
    setBusy(true);
    setError(null);
    try {
      const round = await hiringApi.createRound(input, org);
      // The round starts with a real welcome message, not an empty one; if this
      // write fails the round still exists, and the owner can write their own.
      await hiringApi.updateRound(round.id, { welcomeMessage: defaultWelcome(round.roleName) }, org).catch(() => null);
      refresh();
      setCreating(false);
      navigate(`/app/hiring/rounds/${round.id}`);
    } catch (err) {
      setError(hiringMessage(err, 'The round could not be created'));
    } finally {
      setBusy(false);
    }
  };

  if (access.isLoading) {
    return (
      <Box sx={{ p: '24px', display: 'flex', flexDirection: 'column', gap: '12px', ...fadeInSx }}>
        <Bone width="40%" height={24} />
        <Bone height={72} />
        <Bone height={72} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        bgcolor: '#0F0F0F',
        px: '25px',
        // The layout already leaves 25px under the header; more would float the intro line in a band of its own.
        pt: 0,
        pb: '16px',
        gap: '12px',
        [MOBILE_SX]: { height: 'auto', overflow: 'visible', px: 0, pt: 0 }
      }}
    >
      <Typography sx={{ ...BODY, flexShrink: 0 }}>
        Elliott assesses candidates against the capabilities required for their role so that you can review, compare,
        and decide.
      </Typography>

      <Box
        role="tablist"
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          p: '10px 14px',
          bgcolor: '#1C1C1C',
          border: '1px solid #2A2A2A',
          borderRadius: '8px',
          [MOBILE_SX]: { flexWrap: 'wrap', gap: '10px', px: '12px' }
        }}
      >
        {STATUS_TABS.map((item, i) => (
          <React.Fragment key={item.key}>
            {i > 0 && <Box sx={DIVIDE} />}
            <TabButton
              label={item.label}
              size={isMobileApp ? 14 : 17}
              active={tab === item.key}
              onClick={() => setTab(item.key)}
            />
          </React.Fragment>
        ))}
        {roleOptions.length > 0 && (
          <ScopeFilter
            teams={roleOptions}
            scope={roleScope}
            onChange={setRoleScope}
            withOrganization={false}
            noun="Role"
          />
        )}
        <Box sx={{ flex: 1, [MOBILE_SX]: { display: 'none' } }} />
        <ActionButton
          primary
          disabled={needsNotice || readyRoles.length === 0}
          reason={
            needsNotice
              ? 'Confirm the "Using AI in Hiring" notice first'
              : readyRoles.length === 0
              ? "No role has its capabilities and levels confirmed yet, so candidates couldn't be graded"
              : undefined
          }
          onClick={() => setCreating(true)}
        >
          New Hiring Round
        </ActionButton>
      </Box>

      {needsNotice && (
        <Note>
          Before you create a hiring round, please read and confirm the &quot;Using AI in Hiring&quot; notice.{' '}
          <Box
            component="button"
            type="button"
            onClick={() => setNoticeOpen(true)}
            sx={{
              background: 'none',
              border: 0,
              p: 0,
              color: BRANDY,
              fontFamily: FONT,
              fontSize: 12.5,
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            Read it now
          </Box>
        </Note>
      )}

      {error && !creating && (
        <Typography sx={{ ...BODY, color: DANGER }} role="alert">
          {error}
        </Typography>
      )}

      <Box
        role="tabpanel"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          p: '16px',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#1C1C1C',
          border: `1px solid ${BRANDY_LINE}`,
          borderRadius: '8px',
          boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)',
          [MOBILE_SX]: { flex: '0 0 auto', overflow: 'visible', p: '12px' }
        }}
      >
        {rounds.isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', ...fadeInSx }}>
            <Bone height={72} />
            <Bone height={72} />
          </Box>
        ) : shown.length === 0 ? (
          <Empty>
            {(rounds.data || []).length === 0
              ? 'No Hiring Rounds yet. Create one for a role whose capability levels are confirmed.'
              : filtered
              ? `No ${tab} Hiring Round for the roles picked.`
              : `No ${tab} Hiring Rounds.`}
          </Empty>
        ) : (
          <Box
            sx={{
              flex: '0 1 auto',
              minHeight: 0,
              border: GRID_LINE,
              borderRadius: '4px',
              overflow: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#565656 transparent'
            }}
          >
            <Box sx={COLUMNS}>
              <RoundHeader status={tab} />
              {shown.map((round, i) => (
                <RoundRow
                  key={round.id}
                  round={round}
                  last={i === shown.length - 1}
                  onOpen={() => navigate(`/app/hiring/rounds/${round.id}`)}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Note>
        Only hired candidates&apos; data is passed to your organization&apos;s Dashboard and Insights; all other
        candidate data stays in Hiring for {retention} before being permanently deleted in accordance with data laws.
        None of this data is used to train any AI model.
      </Note>

      {access.data && (
        <OwnerNoticeDialog
          open={noticeOpen && needsNotice}
          block={access.data.ownerAcknowledgement}
          busy={busy}
          error={error}
          onConfirm={confirmNotice}
          onClose={() => setNoticeOpen(false)}
        />
      )}
      {access.data && (
        <CreateRoundDialog
          open={creating}
          roles={roles}
          blockedCountries={access.data.blockedRoleCountries}
          blockedMessage={access.data.blockedRoleMessage}
          busy={busy}
          error={error}
          onCreate={create}
          onClose={() => {
            setCreating(false);
            setError(null);
          }}
        />
      )}
    </Box>
  );
};

export default HiringPage;
